import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  IconButton,
  Chip,
  SelectChangeEvent,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';

// Define types for conditions and actions
interface Condition {
  id: string;
  subject: string;
  indicator: string;
  operator: string;
  value: string;
}

interface Action {
  id: string;
  type: string;
  asset: string;
  quantity: string;
  price?: string;
}

interface Rule {
  id: string;
  trigger: 'IF' | 'WHEN';
  conditions: Condition[];
  actions: Action[];
}

interface VisualStrategyBuilderProps {
  initialRules?: Rule[];
  onSave: (rules: Rule[]) => void;
  onTest: (rules: Rule[]) => void;
}

export const VisualStrategyBuilder: React.FC<VisualStrategyBuilderProps> = ({
  initialRules = [],
  onSave,
  onTest
}) => {
  const [rules, setRules] = useState<Rule[]>(initialRules.length > 0 ? initialRules : [createEmptyRule()]);
  const [error, setError] = useState<string | null>(null);

  // Available options for dropdown selectors
  const subjects = ['BTC', 'ETH', 'LTC', 'SOL', 'ADA', 'AVAX', 'DOT', 'MATIC', 'XRP', 'DOGE'];
  const indicators = ['price', 'RSI', 'MACD', 'Moving Average', 'Volume', 'Market Cap'];
  const operators = ['equals', 'greater than', 'less than', 'crosses above', 'crosses below'];
  const actionTypes = ['BUY', 'SELL', 'LIMIT BUY', 'LIMIT SELL'];

  // Helper function to create a new rule
  function createEmptyRule(): Rule {
    return {
      id: `rule-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      trigger: 'IF',
      conditions: [createEmptyCondition()],
      actions: [createEmptyAction()]
    };
  }

  // Helper function to create a new condition
  function createEmptyCondition(): Condition {
    return {
      id: `condition-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      subject: '',
      indicator: '',
      operator: '',
      value: ''
    };
  }

  // Helper function to create a new action
  function createEmptyAction(): Action {
    return {
      id: `action-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      type: '',
      asset: '',
      quantity: ''
    };
  }

  // Add a new rule
  const addRule = () => {
    setRules([...rules, createEmptyRule()]);
  };

  // Delete a rule
  const deleteRule = (ruleId: string) => {
    if (rules.length <= 1) {
      setError('You must have at least one rule');
      return;
    }
    setRules(rules.filter(rule => rule.id !== ruleId));
    setError(null);
  };

  // Add a condition to a rule
  const addCondition = (ruleId: string) => {
    setRules(rules.map(rule => {
      if (rule.id === ruleId) {
        return {
          ...rule,
          conditions: [...rule.conditions, createEmptyCondition()]
        };
      }
      return rule;
    }));
  };

  // Delete a condition from a rule
  const deleteCondition = (ruleId: string, conditionId: string) => {
    setRules(rules.map(rule => {
      if (rule.id === ruleId) {
        if (rule.conditions.length <= 1) {
          setError('Each rule must have at least one condition');
          return rule;
        }
        return {
          ...rule,
          conditions: rule.conditions.filter(condition => condition.id !== conditionId)
        };
      }
      return rule;
    }));
  };

  // Add an action to a rule
  const addAction = (ruleId: string) => {
    setRules(rules.map(rule => {
      if (rule.id === ruleId) {
        return {
          ...rule,
          actions: [...rule.actions, createEmptyAction()]
        };
      }
      return rule;
    }));
  };

  // Delete an action from a rule
  const deleteAction = (ruleId: string, actionId: string) => {
    setRules(rules.map(rule => {
      if (rule.id === ruleId) {
        if (rule.actions.length <= 1) {
          setError('Each rule must have at least one action');
          return rule;
        }
        return {
          ...rule,
          actions: rule.actions.filter(action => action.id !== actionId)
        };
      }
      return rule;
    }));
  };

  // Update rule trigger type (IF/WHEN)
  const updateRuleTrigger = (ruleId: string, trigger: 'IF' | 'WHEN') => {
    setRules(rules.map(rule => {
      if (rule.id === ruleId) {
        return {
          ...rule,
          trigger
        };
      }
      return rule;
    }));
  };

  // Update condition
  const updateCondition = (ruleId: string, conditionId: string, field: keyof Condition, value: string) => {
    setRules(rules.map(rule => {
      if (rule.id === ruleId) {
        return {
          ...rule,
          conditions: rule.conditions.map(condition => {
            if (condition.id === conditionId) {
              return {
                ...condition,
                [field]: value
              };
            }
            return condition;
          })
        };
      }
      return rule;
    }));
  };

  // Update action
  const updateAction = (ruleId: string, actionId: string, field: keyof Action, value: string) => {
    setRules(rules.map(rule => {
      if (rule.id === ruleId) {
        return {
          ...rule,
          actions: rule.actions.map(action => {
            if (action.id === actionId) {
              const updatedAction = {
                ...action,
                [field]: value
              };

              // Add price field for LIMIT orders
              if (field === 'type' && (value === 'LIMIT BUY' || value === 'LIMIT SELL')) {
                if (!updatedAction.price) {
                  updatedAction.price = '';
                }
              }

              return updatedAction;
            }
            return action;
          })
        };
      }
      return rule;
    }));
  };

  // Validate the strategy before saving
  const validateStrategy = (): boolean => {
    for (const rule of rules) {
      // Check if trigger is selected
      if (!rule.trigger) {
        setError('Every rule must start with IF or WHEN');
        return false;
      }

      // Check conditions
      for (const condition of rule.conditions) {
        if (!condition.subject || !condition.indicator || !condition.operator || !condition.value) {
          setError('All condition fields must be filled');
          return false;
        }
      }

      // Check actions
      for (const action of rule.actions) {
        if (!action.type || !action.asset || !action.quantity) {
          setError('All action fields must be filled');
          return false;
        }

        // Check price for limit orders
        if ((action.type === 'LIMIT BUY' || action.type === 'LIMIT SELL') && !action.price) {
          setError('Limit orders must specify a price');
          return false;
        }
      }
    }

    setError(null);
    return true;
  };

  // Handle saving the strategy
  const handleSave = () => {
    if (validateStrategy()) {
      onSave(rules);
    }
  };

  // Handle testing the strategy
  const handleTest = () => {
    if (validateStrategy()) {
      onTest(rules);
    }
  };

  // Convert rules to human-readable format
  const rulesAsText = () => {
    return rules.map(rule => {
      const conditionsText = rule.conditions.map(condition => 
        `${condition.subject} ${condition.indicator} ${condition.operator} ${condition.value}`
      ).join(' AND ');
      
      const actionsText = rule.actions.map(action => {
        if (action.type.includes('LIMIT')) {
          return `${action.type} ${action.quantity} ${action.asset} at price ${action.price}`;
        }
        return `${action.type} ${action.quantity} ${action.asset}`;
      }).join(', THEN ');
      
      return `${rule.trigger} ${conditionsText} THEN ${actionsText}`;
    }).join('\n\n');
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {rules.map((rule, ruleIndex) => (
        <Paper 
          key={rule.id} 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 4, 
            border: '1px solid #e0e0e0',
            position: 'relative'
          }}
        >
          <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
            <IconButton 
              color="error" 
              onClick={() => deleteRule(rule.id)}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          <Typography variant="h6" gutterBottom>
            Rule {ruleIndex + 1}
          </Typography>

          {/* Trigger selection (IF/WHEN) */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Start with</InputLabel>
                <Select
                  value={rule.trigger}
                  label="Start with"
                  onChange={(e) => updateRuleTrigger(rule.id, e.target.value as 'IF' | 'WHEN')}
                >
                  <MenuItem value="IF">IF</MenuItem>
                  <MenuItem value="WHEN">WHEN</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Conditions */}
          <Typography variant="subtitle1" gutterBottom>
            Conditions
          </Typography>
          
          {rule.conditions.map((condition, condIndex) => (
            <Grid 
              container 
              spacing={2} 
              key={condition.id} 
              alignItems="center"
              sx={{ mb: 2 }}
            >
              {condIndex > 0 && (
                <Grid item xs={12}>
                  <Chip label="AND" color="primary" size="small" />
                </Grid>
              )}
              
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Asset</InputLabel>
                  <Select
                    value={condition.subject}
                    label="Asset"
                    onChange={(e) => updateCondition(rule.id, condition.id, 'subject', e.target.value)}
                  >
                    {subjects.map(subject => (
                      <MenuItem key={subject} value={subject}>{subject}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Indicator</InputLabel>
                  <Select
                    value={condition.indicator}
                    label="Indicator"
                    onChange={(e) => updateCondition(rule.id, condition.id, 'indicator', e.target.value)}
                  >
                    {indicators.map(indicator => (
                      <MenuItem key={indicator} value={indicator}>{indicator}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Operator</InputLabel>
                  <Select
                    value={condition.operator}
                    label="Operator"
                    onChange={(e) => updateCondition(rule.id, condition.id, 'operator', e.target.value)}
                  >
                    {operators.map(operator => (
                      <MenuItem key={operator} value={operator}>{operator}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Value"
                  fullWidth
                  size="small"
                  value={condition.value}
                  onChange={(e) => updateCondition(rule.id, condition.id, 'value', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={1}>
                <IconButton 
                  color="error" 
                  onClick={() => deleteCondition(rule.id, condition.id)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          
          <Button 
            startIcon={<AddIcon />} 
            onClick={() => addCondition(rule.id)}
            sx={{ mb: 3 }}
            variant="outlined"
            size="small"
          >
            Add Condition
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* Actions section */}
          <Typography variant="subtitle1" gutterBottom>
            Then
          </Typography>
          
          {rule.actions.map((action, actionIndex) => (
            <Grid 
              container 
              spacing={2} 
              key={action.id} 
              alignItems="center"
              sx={{ mb: 2 }}
            >
              {actionIndex > 0 && (
                <Grid item xs={12}>
                  <Chip label="AND" color="secondary" size="small" />
                </Grid>
              )}
              
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={action.type}
                    label="Action"
                    onChange={(e) => updateAction(rule.id, action.id, 'type', e.target.value)}
                  >
                    {actionTypes.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Asset</InputLabel>
                  <Select
                    value={action.asset}
                    label="Asset"
                    onChange={(e) => updateAction(rule.id, action.id, 'asset', e.target.value)}
                  >
                    {subjects.map(subject => (
                      <MenuItem key={subject} value={subject}>{subject}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Quantity"
                  fullWidth
                  size="small"
                  value={action.quantity}
                  onChange={(e) => updateAction(rule.id, action.id, 'quantity', e.target.value)}
                />
              </Grid>
              
              {(action.type === 'LIMIT BUY' || action.type === 'LIMIT SELL') && (
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Price"
                    fullWidth
                    size="small"
                    value={action.price || ''}
                    onChange={(e) => updateAction(rule.id, action.id, 'price', e.target.value)}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sm={1}>
                <IconButton 
                  color="error" 
                  onClick={() => deleteAction(rule.id, action.id)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          
          <Button 
            startIcon={<AddIcon />} 
            onClick={() => addAction(rule.id)}
            sx={{ mb: 3 }}
            variant="outlined"
            size="small"
          >
            Add Action
          </Button>
        </Paper>
      ))}

      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Button 
          variant="outlined" 
          startIcon={<AddIcon />}
          onClick={addRule}
        >
          Add Rule
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
        >
          Save Strategy
        </Button>
        <Button 
          variant="contained" 
          color="secondary"
          startIcon={<PlayArrowIcon />}
          onClick={handleTest}
        >
          Test Strategy
        </Button>
      </Box>

      <Paper sx={{ p: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom>
          Strategy Preview
        </Typography>
        <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 1, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          {rulesAsText()}
        </Box>
      </Paper>
    </Box>
  );
};

export default VisualStrategyBuilder;
